import type { KeyValueStore } from '#lib/key-value-store.js';

import { getEnv } from '#env-schema.js';
import { performFlowFetch, toAbsolutePath } from '#lib/flow-utils.js';
import { createState } from '#lib/signals-utils.js';

const STORAGE_KEY = 'elara_invite_code';
// Consent-version keys are intentionally shared across platforms: they gate
// legal compliance (terms/privacy acceptance) and must round-trip identically
// on web and React Native (ELARA-691).
const TERMS_VERSION_KEY = 'elara_pending_accepted_terms_version';
const PRIVACY_VERSION_KEY = 'elara_pending_accepted_privacy_version';

/**
 * @param keyValueStore - Platform persistence port (web localStorage / RN MMKV, ELARA-691).
 * @param fetchImpl - The flow transport's injectable fetch port (ELARA-688/820). Web leaves it
 *   undefined and falls back to `globalThis.fetch`; React Native injects `expo/fetch` at its
 *   composition root so invite validation rides the same native-safe transport as every flow.
 */
export function createInviteState(keyValueStore: KeyValueStore, fetchImpl?: typeof fetch) {
  return createState({
    inviteCodeInput: '',
    validatedInviteCode: '' as string | undefined,
    hasValidInviteSession: false,
    isValidating: false,
    error: undefined as string | undefined,
    pendingAcceptedTermsVersion: '' as string | undefined,
    pendingAcceptedPrivacyVersion: '' as string | undefined,

    setInviteCodeInput(code: string) {
      this.inviteCodeInput = code;
      this.error = undefined;
    },

    getValidatedInviteCode(): string | undefined {
      return this.validatedInviteCode || undefined;
    },

    /**
     * Capture the policy versions the user accepted at signup-time so they survive the
     * signup -> onboarding handoff (Firebase auth happens before profile creation).
     */
    setPendingAcceptance(termsVersion: string, privacyVersion: string) {
      this.pendingAcceptedTermsVersion = termsVersion;
      this.pendingAcceptedPrivacyVersion = privacyVersion;
      keyValueStore.set(TERMS_VERSION_KEY, termsVersion);
      keyValueStore.set(PRIVACY_VERSION_KEY, privacyVersion);
    },

    getPendingAcceptance(): { termsVersion: string; privacyVersion: string } | undefined {
      const terms = this.pendingAcceptedTermsVersion;
      const privacy = this.pendingAcceptedPrivacyVersion;
      if (!terms || !privacy) {
        console.warn('[InviteState] Pending acceptance incomplete', { terms: !!terms, privacy: !!privacy });
        return undefined;
      }
      return { termsVersion: terms, privacyVersion: privacy };
    },

    loadFromStorage() {
      const stored = keyValueStore.get(STORAGE_KEY);
      if (stored && stored.trim().length > 0) {
        this.validatedInviteCode = stored.trim();
        this.hasValidInviteSession = true;
      }
      const storedTerms = keyValueStore.get(TERMS_VERSION_KEY);
      if (storedTerms) this.pendingAcceptedTermsVersion = storedTerms;
      const storedPrivacy = keyValueStore.get(PRIVACY_VERSION_KEY);
      if (storedPrivacy) this.pendingAcceptedPrivacyVersion = storedPrivacy;
    },

    /**
     * Forget a code the server has rejected (already used, disabled, expired, unknown). Unlike
     * {@link clearInviteCode} this always removes the persisted copy: a rejected code that lingers in
     * storage is reloaded on the next visit, auto-submitted again, and silently shadows any fresh code
     * the member is given — which is exactly how two members got stuck retrying a burned code.
     */
    discardInviteCode() {
      this.validatedInviteCode = '';
      this.hasValidInviteSession = false;
      keyValueStore.remove(STORAGE_KEY);
    },

    /** Clear stored invite code and pending acceptance after successful profile creation */
    clearInviteCode() {
      this.validatedInviteCode = '';
      this.hasValidInviteSession = false;
      this.pendingAcceptedTermsVersion = '';
      this.pendingAcceptedPrivacyVersion = '';
      if (getEnv().MODE === 'development') return;
      keyValueStore.remove(STORAGE_KEY);
      keyValueStore.remove(TERMS_VERSION_KEY);
      keyValueStore.remove(PRIVACY_VERSION_KEY);
    },

    async validateInviteCode(code?: string) {
      const raw = (code ?? this.inviteCodeInput ?? '').trim();
      if (!raw) {
        this.error = 'Please enter an invite code';
        return;
      }

      this.isValidating = true;
      this.error = undefined;

      try {
        // Invite validation runs pre-auth — before any Firebase account (and therefore any Bearer
        // token) exists — so it cannot ride the token-gated /api/flows/app proxy. It targets the
        // dedicated public /api/invite/validate endpoint, but reuses the flow transport's two native
        // seams so web and React Native share one path (ELARA-820):
        //   - `toAbsolutePath` resolves the relative path against VITE_FLOW_API_BASE_URL. Web leaves
        //     the base unset and resolves against the page origin; React Native has no origin, so the
        //     absolute Cloud Run URL is mandatory or the request fails to resolve.
        //   - `performFlowFetch` uses the injected `fetchImpl` (`expo/fetch` on RN), falling back to
        //     `globalThis.fetch` on web — the same port every flow proxy uses (ELARA-688).
        // No cookie is sent: the endpoint is public, and the old `credentials: 'include'` never
        // carried a cookie on React Native anyway, which is what broke the native invite gate.
        const baseUrl = getEnv().VITE_FLOW_API_BASE_URL?.trim() || undefined;
        const endpoint = toAbsolutePath('/api/invite/validate', baseUrl);

        const response = await performFlowFetch(fetchImpl, endpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ inviteCode: raw }),
        });

        if (!response.ok) {
          throw new Error(`Invite validation failed with status ${response.status}`);
        }

        const data = (await response.json()) as { valid?: boolean };

        if (data.valid) {
          this.validatedInviteCode = raw;
          this.hasValidInviteSession = true;
          keyValueStore.set(STORAGE_KEY, raw);
        } else {
          // A definitive rejection also evicts any previously stored code, so the signup form asks
          // for a code again instead of resurrecting the stale one on reload.
          this.discardInviteCode();
          this.error = 'This invite code is not valid.';
        }
      } catch (error) {
        console.error('[InviteState] Failed to validate invite code', error);
        this.validatedInviteCode = '';
        this.hasValidInviteSession = false;
        this.error = 'Unable to validate invite code. Please try again.';
      } finally {
        this.isValidating = false;
      }
    },
  });
}

export type InviteState = ReturnType<typeof createInviteState>;
